import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";

import {
  ATTACHMENT_TEXT_EXTRACTION_WARNING_SOCKET_EVENT,
  ATTACHMENTS_OMITTED_SOCKET_EVENT,
  AUTH_ERROR_PATTERNS,
  AUTH_RECONNECT_THROTTLE_MS,
  AUTO_CONFIRMATION_REQUEST_SOCKET_EVENT,
  AUTO_CONFIRMATION_RESOLVED_SOCKET_EVENT,
  AUTO_CONFIRMATION_RESPONSE_SOCKET_EVENT,
  CONTEXT_PRESSURE_SOCKET_EVENT,
  HEALTH_CHECK_ACK_TIMEOUT_MS,
  INTEGRATION_CONNECT_PROMPT_SOCKET_EVENT,
  MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITH_FILES,
  MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES,
  NEW_CHAT_SEND_BUCKET_KEY,
  PENDING_MESSAGE_TTL_MS,
  PII_IMAGE_MASKED_SOCKET_EVENT,
  STREAM_BUFFER_PASSTHROUGH_EVENTS,
  TOOL_CALL_END_SOCKET_EVENT,
  WS_AUTH_UNAUTHORIZED_ERROR_CODE,
} from "../constants/chat";
import { LICENSE_EXPIRED_ERROR_CODE } from "../constants/license";
import { SKILL_SCRIPT_RUN_SOCKET_EVENT } from "../constants/skill-sandbox";
import { SKILL_ACTIVATIONS_SOCKET_EVENT } from "../constants/skills";
import { HTML_PAGE_RENDER_ERROR_EVENT } from "../lib/html-preview/constants";
import type { IAttachmentsOmittedPayload } from "../types/attachments-omitted";
import type {
  IAutoConfirmationRequestPayload,
  IAutoConfirmationResolvedPayload,
  IChat,
  IChatTitleGenerated,
  IDeepSearchProgressPayload,
  IHtmlPageRenderErrorPayload,
  IImprovePromptPayload,
  IIntegrationConnectPromptPayload,
  IKnowledgeSourcesPayload,
  IMessage,
  IMessageContentUpdatePayload,
  IMessageDeltaPayload,
  IMessagePayload,
  IMessagePreparationProgressPayload,
  IPendingMessage,
  IPiiImageMaskedPayload,
  IPromptImproveDeltaPayload,
  IPromptImproveErrorPayload,
  IPromptImproveFinishPayload,
  IReasoningStepPayload,
  IRequestMultiModelSummaryPayload,
  ISendAutoConfirmationResponseParams,
  ISendFailurePayload,
  ISendMessagePayload,
  ISendToolApprovalParams,
  IStreamErrorPayload,
  IStreamFinishPayload,
  IToolApprovalRequestPayload,
  IToolApprovalRespondedPayload,
  IToolCallEndPayload,
  IToolCallPayload,
  IToolFileErrorPayload,
  IToolFilePayload,
  IWebSearchProgressPayload,
  IWebSocketError,
  IWebSocketServiceConfig,
} from "../types/chat";
import type { IContextPressurePayload } from "../types/context-pressure";
import type { TSkillScriptRunEvent } from "../types/skill-sandbox";
import type { TSkillActivationsEvent } from "../types/skills";
import type { IAttachmentTextExtractionWarningPayload } from "../types/text-extraction";
import {
  decidePendingSend,
  describeRequestError,
  getClientTimezone,
  shouldDeferAuthReconnectForActiveStream,
  SocketAuthRecovery,
  StorageService,
  stripSelectionsForWire,
} from "../utils";
import type {
  IAddSocketEventListenerProps,
  IGetSocketListenerCountProps,
  TSocketEventListener,
} from "./websocket.types";

export class WebSocketService {
  private static instance: WebSocketService;
  private socket: Socket | null = null;
  private url: string;
  private getUrl?: () => string;
  // NOTE: NSAI-4330 — the URL the current `socket` was built for. A socket.io
  // client keeps its URL for life, and this singleton outlives a sign-out, so
  // a `connect()` after a runtime tenant switch has to notice the change and
  // rebuild rather than resume the socket to the previous deployment.
  private connectedUrl: string | null = null;
  private storageService: StorageService;
  private getLanguage?: () => string | null;
  private getProjectUuid?: () => string | null;
  private onTokenRefresh?: () => Promise<string | null>;
  private getTestScenario?: () => string | null;
  private onFlushStreamBuffer?: () => void;
  private pendingMessages: IPendingMessage[] = [];
  private isRefreshing = false;
  // NOTE: A refresh can finish after the owning socket was closed. Increment
  // this generation on teardown so that stale completions cannot restart
  // recovery or reconnect a socket the caller deliberately stopped.
  private authRefreshGeneration = 0;
  // NOTE: NSAI-5042 — consumers (chat hook, composer) subscribe here to learn
  // that a buffered/optimistic send definitively failed, so they can roll back
  // optimistic messages, surface an error, and restore the draft.
  private readonly sendFailureListeners = new Set<
    (payload: ISendFailurePayload) => void
  >();
  // NOTE: NSAI-5042 — when a chat's sends are cancelled (user Stop, stall
  // watchdog), an emit still awaiting its ack must not resurrect itself by
  // buffering on the ack timeout. Keyed by chatId, value = cancel time.
  private readonly cancelledSendAtByChatId = new Map<string, number>();
  // NOTE: NSAI-5042 — when a chat's last send was ACKED, the server owns the
  // request: the client can never guarantee non-delivery, so the stall
  // watchdog must keep waiting instead of declaring a failure that could
  // invite a duplicate re-send (PR #4696 review). Keyed by chatId, value =
  // ack time; pruned alongside the cancel markers.
  private readonly ackedSendAtByChatId = new Map<string, number>();
  // NOTE: NSAI-5042 — sends emitted but not yet ack-resolved, per chat. The
  // stall watchdog consults this so a send that is mid-flight (not in the
  // buffer) at fire time still counts as unresolved and gets surfaced.
  private readonly inflightSendCountByChatId = new Map<string, number>();
  // NOTE: NSAI-5042 — throttles auth-exception-driven reconnect cycles so a
  // rejection a fresh token cannot fix does not loop refresh/reconnect.
  private lastAuthReconnectAtMs = 0;
  // NOTE: NSAI-5910 — bounds the refresh + reconnect cycle a refused handshake
  // starts; see SocketAuthRecovery.
  private readonly authRecovery: SocketAuthRecovery;
  private currentChatId: string | null = null;
  // NOTE: NSAI-4786 — one place that owns every consumer subscription, keyed by
  // event name. Handlers used to be attached to whatever `this.socket` was at the
  // time, so after a socket was swapped out an `off()` could target the wrong
  // instance. The handler stayed on the old socket and the same event got applied
  // twice, which is what duplicated tokens look like. Keeping subscriptions here
  // means a new socket re-attaches them all, tearing one down detaches them all,
  // and a Set makes registering the same handler twice a no-op.
  private readonly eventListeners: Map<string, Set<TSocketEventListener>> =
    new Map();
  private hasActiveStream?: () => boolean;
  private customGenerateRequestId?: () => string;
  private beforeSendMessage?: () => void;
  private getAckTimeoutMsOverride?: () => number | undefined;
  private getHealthCheckTimeoutMsOverride?: () => number | undefined;
  private onLicenseExpired?: () => void;
  // NOTE: NSAI-5117 — set when a token refresh landed while a reply was still
  // streaming. `socket.auth` already carries the fresh token; only the physical
  // handshake cycle waits, and it runs as soon as the stream settles — see
  // scheduleDeferredAuthReconnectCheck / flushDeferredAuthReconnect.
  private pendingAuthReconnect = false;

  // NOTE: NSAI-5117 — teardown hooks for consumers that hold server-side state
  // (room membership) and must unwind it while the socket is still open.
  private readonly beforeDisconnectHandlers: Set<() => void> = new Set();
  // NOTE: NSAI-5117 — the last send made before the chat had a uuid, so a
  // consumer can render it optimistically after the chat is created.
  private _lastNewChatMessage: ISendMessagePayload | null = null;

  private constructor({
    url,
    getUrl,
    storageAdapter,
    getLanguage,
    getProjectUuid,
    onTokenRefresh,
    getTestScenario,
    onFlushStreamBuffer,
    hasActiveStream,
    generateRequestId,
    beforeSendMessage,
    getAckTimeoutMsOverride,
    getHealthCheckTimeoutMsOverride,
    onLicenseExpired,
    activitySource,
  }: IWebSocketServiceConfig) {
    this.url = url;
    this.getUrl = getUrl;
    this.storageService = new StorageService(storageAdapter);
    this.getLanguage = getLanguage;
    this.getProjectUuid = getProjectUuid;
    this.onTokenRefresh = onTokenRefresh;
    this.getTestScenario = getTestScenario;
    this.onFlushStreamBuffer = onFlushStreamBuffer;
    this.hasActiveStream = hasActiveStream;
    this.customGenerateRequestId = generateRequestId;
    this.beforeSendMessage = beforeSendMessage;
    this.getAckTimeoutMsOverride = getAckTimeoutMsOverride;
    this.getHealthCheckTimeoutMsOverride = getHealthCheckTimeoutMsOverride;
    this.onLicenseExpired = onLicenseExpired;
    this.authRecovery = new SocketAuthRecovery({
      recover: () => {
        void this.handleAuthError();
      },
      activitySource,
    });
  }

  static getInstance(config: IWebSocketServiceConfig): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService(config);
    }
    return WebSocketService.instance;
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // NOTE: NSAI-5117 — a host that keeps its own per-chat listeners (web's
  // background tracker) needs to know whether a socket object exists at all,
  // which is not the same question as `isConnected`: a socket that exists but
  // is mid-reconnect can still take subscriptions.
  get hasSocket(): boolean {
    return this.socket !== null;
  }

  // NOTE: NSAI-5117 — the room the foreground consumer is currently in.
  get activeChatId(): string | null {
    return this.currentChatId;
  }

  /**
   * NOTE: NSAI-5117 — re-enters a room WITHOUT claiming it as the current chat.
   * Background trackers restore their rooms after a reconnect, and that restore
   * must not overwrite the foreground chat's ownership of `currentChatId`.
   */
  rejoinChatRoom(chatId: string): void {
    if (!this.socket) return;
    this.socket.emit("joinChat", chatId);
  }

  get lastNewChatMessage(): ISendMessagePayload | null {
    return this._lastNewChatMessage;
  }

  clearLastNewChatMessage(): void {
    this._lastNewChatMessage = null;
  }

  /**
   * Registers a consumer handler and attaches it to the live socket.
   * The returned unsubscriber removes it from both the registry and the socket.
   */
  private addEventListener({
    event,
    listener,
  }: IAddSocketEventListenerProps): () => void {
    const listenersForEvent =
      this.eventListeners.get(event) ?? new Set<TSocketEventListener>();
    // NOTE: NSAI-4786 — the registry dedupes by reference, so the socket must be
    // attached only on a genuinely new listener. Attaching twice would leave one
    // copy behind after `off()` (which removes a single match), reintroducing the
    // doubled-event symptom this registry exists to prevent.
    const wasNewlyRegistered = !listenersForEvent.has(listener);
    listenersForEvent.add(listener);
    this.eventListeners.set(event, listenersForEvent);
    if (wasNewlyRegistered) {
      this.socket?.on(event, listener);
    }

    return () => {
      const currentListeners = this.eventListeners.get(event);
      if (currentListeners) {
        currentListeners.delete(listener);
        if (currentListeners.size === 0) {
          this.eventListeners.delete(event);
        }
      }
      this.socket?.off(event, listener);
    };
  }

  /**
   * Re-attaches every registered handler to a freshly created socket, so a
   * reconnect or a project switch never leaves consumers deaf.
   */
  private attachRegisteredListeners(): void {
    if (!this.socket) return;

    for (const [event, listenersForEvent] of this.eventListeners) {
      for (const listener of listenersForEvent) {
        this.socket.on(event, listener);
      }
    }
  }

  /**
   * Number of handlers currently attached for an event.
   * NOTE: NSAI-4786 — diagnostic hook. More handlers than mounted consumers
   * means listeners are leaking, which is how duplicated tokens show up.
   */
  getListenerCount({ event }: IGetSocketListenerCountProps): number {
    return this.eventListeners.get(event)?.size ?? 0;
  }

  /**
   * Generates a unique request ID for message deduplication
   */
  private generateRequestId(): string {
    return (
      this.customGenerateRequestId?.() ??
      `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    );
  }

  /**
   * Checks if error is authentication related
   * Uses specific error codes and patterns to avoid false positives
   */
  private isAuthError(error: Error): boolean {
    // NOTE: Check if error is authentication related
    // using specific error codes and patterns to avoid false positives
    const errorWithCode = error as Error & {
      code?: string | number;
      status?: number;
      statusCode?: number;
    };
    const errorCode =
      errorWithCode.code || errorWithCode.status || errorWithCode.statusCode;

    if (
      // NOTE:  error code can be in string or number format
      errorCode == 401 ||
      errorCode === "ERR_JWT_EXPIRED" ||
      errorCode === "ERR_INVALID_TOKEN" ||
      errorCode === "ERR_UNAUTHORIZED"
    ) {
      return true;
    }

    const errorMessage = error.message?.toLowerCase() || "";

    return AUTH_ERROR_PATTERNS.some((pattern) =>
      errorMessage.includes(pattern),
    );
  }

  /**
   * Handles authentication error by refreshing token and reconnecting
   */
  private async handleAuthError(): Promise<void> {
    if (this.isRefreshing || !this.onTokenRefresh) return;

    const refreshGeneration = this.authRefreshGeneration;
    this.isRefreshing = true;
    try {
      console.debug("WebSocket: Attempting token refresh...");
      const newToken = await this.onTokenRefresh();
      if (refreshGeneration !== this.authRefreshGeneration) return;
      if (newToken) {
        console.debug("WebSocket: Token refreshed successfully");
        this.updateAuthAndReconnect();
      } else {
        console.debug("WebSocket: Token refresh returned null");
        this.notifyPendingMessagesFailed("auth-refresh-failed");
        this.pendingMessages = [];
        this.recordRefreshFailure();
      }
    } catch (error) {
      if (refreshGeneration !== this.authRefreshGeneration) return;
      // NOTE: NSAI-6847 — logged as `{ status, code, message }`, like the API
      // client's own refresh failures: on mobile this refresh runs through the
      // API client, whose failed request carries the refresh token.
      console.debug(
        "WebSocket: Token refresh failed",
        describeRequestError(error),
      );
      this.notifyPendingMessagesFailed("auth-refresh-failed");
      this.pendingMessages = [];
      this.recordRefreshFailure();
    } finally {
      if (refreshGeneration === this.authRefreshGeneration) {
        this.isRefreshing = false;
      }
    }
  }

  // NOTE: NSAI-5910 — a refresh that failed because the session is over has
  // already signed the user out through the API client, which clears the
  // stored token; there is nothing left to reconnect with. Any other failure
  // is retried later by the recovery policy, never in a tight loop.
  private recordRefreshFailure(): void {
    this.authRecovery.recordRefreshFailure({
      isSessionEnded: !this.storageService.getToken(),
    });
  }

  /**
   * Writes the freshest credentials onto the existing socket's handshake.
   * socket.io reads `auth` on every (re)connection attempt, so this is what
   * makes a resumed or deferred reconnect use the current token.
   */
  private applyCurrentAuthToSocket(): boolean {
    const token = this.storageService.getToken();
    const projectUuid = this.getProjectUuid?.();
    const language = this.getLanguage?.();
    // NOTE: NSAI-5470 — re-read rather than carried over from connect(), so an
    // auth-refresh reconnect picks up a zone that changed while the app was open
    // instead of pinning the socket to the zone it first opened in. That covers
    // the two paths that rebuild this payload; socket.io's own automatic
    // reconnect reuses the object by reference and keeps whatever zone was last
    // written here, exactly as it already does for `language` and `projectUuid`.
    const timezone = getClientTimezone();

    if (!token || !this.socket) return false;

    this.socket.auth = {
      token: `Bearer ${token}`,
      projectUuid,
      language,
      timezone,
    };
    return true;
  }

  updateAuthAndReconnect(): void {
    console.debug("WebSocket: Updating auth and reconnecting...");

    if (!this.applyCurrentAuthToSocket() || !this.socket) return;

    // NOTE: NSAI-5910 — this reconnect is the attempt a scheduled recovery
    // retry would have made, so drop the scheduled one.
    this.authRecovery.cancelPendingRetry();

    // NOTE: NSAI-5117 — an unrelated REST token refresh (a background 401 →
    // /auth/refresh while a generation is streaming) must NOT tear down a live
    // socket. A forced disconnect drops the in-flight — often still-empty —
    // assistant message and surfaces a spurious "connection lost". Stream
    // delivery is a server room broadcast that needs no re-auth, so the live
    // stream is safe; the fresh token written above is applied by the deferred
    // reconnect that runs as soon as the stream settles. A client that supplies
    // no `hasActiveStream` never defers.
    if (
      shouldDeferAuthReconnectForActiveStream({
        isConnected: this.socket.connected,
        hasActiveStream: this.hasActiveStream?.() ?? false,
      })
    ) {
      this.pendingAuthReconnect = true;
      console.debug(
        "WebSocket: Deferring auth reconnect — active stream in flight",
      );
      return;
    }

    if (this.socket.connected || this.socket.active) {
      console.debug(
        "WebSocket: Disconnecting before reconnect with new auth...",
      );
      this.socket.disconnect();
    }

    this.socket.connect();
  }

  /**
   * NOTE: NSAI-5117 — re-evaluates a deferred reconnect once a terminal stream
   * event lands. Deferred to a macrotask so the consumer's own cache update
   * (which clears the streaming flag) runs first, otherwise `hasActiveStream`
   * would still report the just-finished reply as live.
   */
  private scheduleDeferredAuthReconnectCheck(): void {
    if (!this.pendingAuthReconnect) return;
    setTimeout(() => this.flushDeferredAuthReconnect(), 0);
  }

  private flushDeferredAuthReconnect(): void {
    if (!this.pendingAuthReconnect || !this.socket) return;

    // NOTE: Another reply started (or this one has not settled yet) — stay
    // deferred and retry on the next terminal event.
    if (this.socket.connected && (this.hasActiveStream?.() ?? false)) return;

    this.pendingAuthReconnect = false;
    console.debug("WebSocket: Running deferred auth reconnect");

    if (this.socket.connected) {
      this.socket.disconnect();
    }

    this.socket.connect();
  }

  /**
   * Sends all pending messages after successful reconnection
   */
  private flushPendingMessages(): void {
    if (this.pendingMessages.length === 0) return;

    console.debug(
      `WebSocket: Flushing ${this.pendingMessages.length} pending message(s)`,
    );

    // NOTE: Re-join current chat if needed
    if (this.currentChatId && this.socket) {
      this.socket.emit("joinChat", this.currentChatId);
    }

    // NOTE: Copy and clear pending messages to avoid double-sending; entries
    // that are still eligible re-buffer themselves below.
    const messagesToFlush = [...this.pendingMessages];
    this.pendingMessages = [];

    const now = Date.now();
    for (const pending of messagesToFlush) {
      const decision = decidePendingSend({
        ageMs: now - pending.timestamp,
        attempts: pending.attempts,
      });

      // NOTE: NSAI-5042 — expired / exhausted entries are no longer silently
      // spliced: the UI is told so it can roll back and restore the draft.
      if (decision === "expire" || decision === "give-up") {
        console.debug(
          `WebSocket: Dropping pending message (${decision}) after ${pending.attempts} attempt(s)`,
        );
        this.notifySendFailure({
          chatId: pending.payload.chatId ?? null,
          content: pending.payload.content,
          reason: decision === "expire" ? "expired" : "ack-failed",
        });
        continue;
      }

      // NOTE: NSAI-5042 — the socket can drop again between the connect event
      // and this loop; re-buffer instead of destroying still-valid messages.
      if (!this.socket?.connected) {
        this.pendingMessages.push(pending);
        continue;
      }

      const retryPayload = { ...pending.payload, isRetry: true };
      const retryEmittedAtMs = Date.now();
      const settleInflightRetry = this.trackInflightSend(
        pending.payload.chatId ?? null,
      );
      console.debug(
        "WebSocket: Re-sending pending message as retry:",
        retryPayload,
      );
      // NOTE: NSAI-5042 — file-aware, mirroring the initial send path.
      const retryAckTimeoutMs =
        this.getAckTimeoutMsOverride?.() ??
        ((retryPayload.files?.length ?? 0) > 0
          ? MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITH_FILES
          : MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES);
      this.socket
        .timeout(retryAckTimeoutMs)
        .emit(
          "sendMessage",
          stripSelectionsForWire(retryPayload),
          (err: Error | null) => {
            settleInflightRetry();
            if (err) {
              // NOTE: NSAI-5042 — the chat was cancelled (Stop / stall
              // watchdog) while this retry awaited its ack — drop it.
              if (
                this.wasSendCancelledSince({
                  chatId: pending.payload.chatId ?? null,
                  sinceMs: retryEmittedAtMs,
                })
              ) {
                console.debug(
                  "WebSocket: retry ack timeout for a cancelled send — dropping",
                );
                return;
              }
              // NOTE: NSAI-5042 — keep the ORIGINAL timestamp so the TTL stays
              // absolute from the first buffering; the next flush applies
              // decidePendingSend again and eventually surfaces the failure.
              console.debug(
                "WebSocket: Retry ack failed, re-buffering:",
                err.message,
              );
              this.pendingMessages.push({
                ...pending,
                attempts: pending.attempts + 1,
              });
              this.verifyConnectionAfterAckTimeout();
            } else {
              console.debug(
                "WebSocket: Pending message acknowledged by server",
              );
              this.recordAckedSend(pending.payload.chatId ?? null);
            }
          },
        );
    }
  }

  // NOTE: NSAI-5042 — surfaces every buffered send as failed before the buffer
  // is dropped (auth refresh dead-end), so the UI can roll back and restore
  // drafts instead of losing input silently.
  private notifyPendingMessagesFailed(
    reason: ISendFailurePayload["reason"],
  ): void {
    for (const pending of this.pendingMessages) {
      this.notifySendFailure({
        chatId: pending.payload.chatId ?? null,
        content: pending.payload.content,
        reason,
      });
    }
  }

  // NOTE: NSAI-5042 — liveness probe after a sendMessage ack timeout. No probe
  // ack ⇒ the transport is a zombie: recycle it (disconnect+connect) so the
  // fresh "connect" event flushes pendingMessages. A healthy probe ack means
  // the server is reachable — leave the buffer for the reconnect flush and let
  // the UI stall watchdog surface the failure to the user.
  private verifyConnectionAfterAckTimeout(): void {
    if (!this.socket) return;
    const socketIdAtProbe = this.socket.id;
    this.socket
      .timeout(
        this.getHealthCheckTimeoutMsOverride?.() ?? HEALTH_CHECK_ACK_TIMEOUT_MS,
      )
      .emit("connectionHealthCheck", (probeErr: Error | null) => {
        if (probeErr) {
          // NOTE: NSAI-5042 — a reconnect may already have replaced this
          // connection while the probe was in flight; recycling the fresh,
          // healthy connection would drop live streams for nothing.
          if (!this.socket || this.socket.id !== socketIdAtProbe) {
            console.debug(
              "WebSocket: stale health probe result ignored — socket already recycled",
            );
            return;
          }
          console.debug(
            "WebSocket: health probe failed after ack timeout — recycling socket",
          );
          this.socket.disconnect();
          this.socket.connect();
          return;
        }
        // NOTE: NSAI-5042 — the transport is healthy, so the lost ack was a
        // server-side rejection (e.g. a stale-token guard) or a dropped packet.
        // Retry the buffer NOW over the live socket instead of waiting for a
        // reconnect that may never come; the server's post-persist dedupe key
        // makes an already-processed requestId a safe no-op.
        this.flushPendingMessages();
      });
  }

  // NOTE: NSAI-5116 — a new chat's first send has no chatId yet, so all the
  // per-chat recovery machinery below buckets those sends under
  // NEW_CHAT_SEND_BUCKET_KEY. Only one new-chat send can be pending at a time
  // per client (the composer is locked by the pending-first-send state), so a
  // single bucket is sufficient and no requestId re-keying is needed.
  private static toSendBucket(chatId: string | null): string {
    return chatId ?? NEW_CHAT_SEND_BUCKET_KEY;
  }

  private trackInflightSend(chatId: string | null): () => void {
    const bucket = WebSocketService.toSendBucket(chatId);
    this.inflightSendCountByChatId.set(
      bucket,
      (this.inflightSendCountByChatId.get(bucket) ?? 0) + 1,
    );
    let isSettled = false;
    return () => {
      if (isSettled) return;
      isSettled = true;
      const current = this.inflightSendCountByChatId.get(bucket) ?? 0;
      if (current <= 1) {
        this.inflightSendCountByChatId.delete(bucket);
      } else {
        this.inflightSendCountByChatId.set(bucket, current - 1);
      }
    };
  }

  // NOTE: NSAI-5042 — true while at least one send for the chat (or, with
  // chatId null, the pending new-chat send) has been emitted but not yet
  // ack-resolved.
  hasInflightSendForChat(props: { chatId: string | null }): boolean {
    return (
      (this.inflightSendCountByChatId.get(
        WebSocketService.toSendBucket(props.chatId),
      ) ?? 0) > 0
    );
  }

  // NOTE: NSAI-5042 — entries older than the pending TTL can no longer match
  // any live ack callback (ack timeouts are far shorter), so prune them
  // opportunistically to keep the map bounded over a long session.
  private pruneCancelledSendMarkers(): void {
    const cutoff = Date.now() - PENDING_MESSAGE_TTL_MS;
    for (const [chatId, cancelledAtMs] of this.cancelledSendAtByChatId) {
      if (cancelledAtMs < cutoff) {
        this.cancelledSendAtByChatId.delete(chatId);
      }
    }
    for (const [chatId, ackedAtMs] of this.ackedSendAtByChatId) {
      if (ackedAtMs < cutoff) {
        this.ackedSendAtByChatId.delete(chatId);
      }
    }
  }

  private recordAckedSend(chatId: string | null): void {
    this.pruneCancelledSendMarkers();
    this.ackedSendAtByChatId.set(
      WebSocketService.toSendBucket(chatId),
      Date.now(),
    );
  }

  // NOTE: NSAI-5042 — true when a send for the chat (or, with chatId null,
  // the pending new-chat send) was acknowledged at or after the given moment.
  wasSendAckedSince(props: {
    chatId: string | null;
    sinceMs: number;
  }): boolean {
    const ackedAtMs = this.ackedSendAtByChatId.get(
      WebSocketService.toSendBucket(props.chatId),
    );
    return ackedAtMs !== undefined && ackedAtMs >= props.sinceMs;
  }

  // NOTE: NSAI-5042 — true when the chat's sends were cancelled after the
  // given moment (user Stop or the stall watchdog declaring failure).
  private wasSendCancelledSince(props: {
    chatId: string | null;
    sinceMs: number;
  }): boolean {
    const cancelledAtMs = this.cancelledSendAtByChatId.get(
      WebSocketService.toSendBucket(props.chatId),
    );
    return cancelledAtMs !== undefined && cancelledAtMs >= props.sinceMs;
  }

  // NOTE: NSAI-5042 — non-mutating peek used by the stall watchdog to decide
  // whether a send is still unresolved BEFORE it commits to the destructive
  // purge + cancel-marker (PR #4696 Copilot review).
  hasPendingSendsForChat(props: { chatId: string | null }): boolean {
    return this.pendingMessages.some(
      (pending) => (pending.payload.chatId ?? null) === props.chatId,
    );
  }

  /**
   * NOTE: NSAI-5042 — removes and returns the buffered send contents for a
   * chat, marking the chat cancelled so an emit still awaiting its ack cannot
   * re-buffer. Called by the stall watchdog when it declares the send failed:
   * a declared failure must never un-fail by delivering later (the user may
   * already have re-sent the restored draft — that would duplicate the
   * message with a different requestId the server cannot deduplicate).
   */
  takePendingSendsForChat(props: { chatId: string | null }): string[] {
    this.pruneCancelledSendMarkers();
    this.cancelledSendAtByChatId.set(
      WebSocketService.toSendBucket(props.chatId),
      Date.now(),
    );
    const taken: string[] = [];
    this.pendingMessages = this.pendingMessages.filter((pending) => {
      if ((pending.payload.chatId ?? null) !== props.chatId) return true;
      taken.push(pending.payload.content);
      return false;
    });
    return taken;
  }

  /**
   * Subscribes to definitive send failures (TTL expiry, retry exhaustion,
   * auth-refresh failure, UI stall watchdog). Returns an unsubscribe function.
   */
  onSendFailure(callback: (payload: ISendFailurePayload) => void): () => void {
    this.sendFailureListeners.add(callback);
    return () => this.sendFailureListeners.delete(callback);
  }

  // NOTE: NSAI-5042 — public so the chat hook's stall watchdog can route its
  // failure through the same channel as the service-internal ones.
  notifySendFailure(payload: ISendFailurePayload): void {
    for (const listener of this.sendFailureListeners) {
      try {
        listener(payload);
      } catch (error) {
        console.error("WebSocket: sendFailure listener threw", error);
      }
    }
  }

  connect(): void {
    const url = this.getUrl?.() ?? this.url;

    if (this.socket && this.connectedUrl !== url) {
      console.debug("WebSocket URL changed, rebuilding the socket");
      this.disconnect();
    }

    // NOTE: NSAI-4786 — an existing socket is always the one to keep, even
    // mid-handshake. The old guard only bailed out on `connected`, so a
    // `connect()` during the handshake built a second `io()` instance and
    // overwrote `this.socket`. The first was already opening, so it lived on as
    // an orphan that could not be disconnected and whose handlers could not be
    // removed. On a cold start `connect()` runs several times while auth and
    // projects settle — exactly when the handshake is in flight, which matches
    // the "duplicated on the first send, clean once warm" reports. socket.io's
    // own `connect()` is safe to call while opening.
    if (this.socket) {
      if (this.socket.connected) {
        console.debug("WebSocket already connected");
      } else {
        // NOTE: NSAI-5117 — refit the handshake with the CURRENT token before
        // resuming. The token may have been rotated by a REST refresh since
        // this socket was built, and socket.io reads `auth` on every attempt,
        // so resuming without this would redial on a stale pass and have the
        // gateway reject the first guarded event.
        this.applyCurrentAuthToSocket();
        console.debug("WebSocket already connecting, resuming existing socket");
        this.socket.connect();
      }
      return;
    }

    const token = this.storageService.getToken();
    const projectUuid = this.getProjectUuid?.();
    const language = this.getLanguage?.();
    // NOTE: NSAI-5470 — the server reads the user's zone off this handshake
    // (`handshake.auth.timezone`) and falls back to UTC when it is absent, which
    // put every mobile turn on a UTC calendar. Unlike `language` this is not a
    // stored preference, so it is read from the platform here rather than
    // injected by the host — that way any consumer of this client gets it.
    const timezone = getClientTimezone();
    const testScenario = this.getTestScenario?.();

    if (!token) {
      console.debug("No authentication token found");
      throw new Error("No authentication token found");
    }

    console.debug("Connecting to WebSocket server...");

    console.debug("WebSocket URL:", url);

    // NOTE: Build extra headers and query params for E2E testing
    const extraHeaders: Record<string, string> = {};
    const query: Record<string, string> = {};
    if (testScenario) {
      extraHeaders["X-Test-Scenario"] = testScenario;
      // NOTE: Also pass via query for transports that don't support custom headers
      query["X-Test-Scenario"] = testScenario;
    }

    this.connectedUrl = url;
    this.socket = io(url, {
      auth: {
        token: `Bearer ${token}`,
        projectUuid,
        language,
        timezone,
      },
      extraHeaders,
      query,
      // NOTE: Large base64 payloads are fragile over long-polling (HTTP request size limits in proxies).
      // Prefer a pure WebSocket transport to avoid huge HTTP payloads and reduce disconnects.
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    // NOTE: NSAI-5909 — per-token frames are coalesced client-side, so every
    // OTHER frame of this socket must see them applied first. `onAny` runs
    // ahead of every named listener, so this one place fixes the order for all
    // consumers. No-op on a client that does not coalesce.
    this.socket.onAny((event: string) => {
      if (!STREAM_BUFFER_PASSTHROUGH_EVENTS.has(event)) {
        this.onFlushStreamBuffer?.();
      }
    });

    this.socket.on("connect", () => {
      console.debug("Connected to WebSocket server");
      // NOTE: NSAI-5117 — a fresh handshake just authenticated with the latest
      // token, so any deferred auth reconnect is now satisfied.
      this.pendingAuthReconnect = false;
      this.authRecovery.recordConnected();
      this.flushPendingMessages();
    });

    // NOTE: NSAI-5117 — when a deferred auth refresh is pending, run the
    // reconnect as soon as streaming settles, so the server-side handshake
    // token is refreshed before the next guarded event (sendMessage /
    // stopMessage / toolApprovalResponse).
    this.socket.on("streamFinish", () =>
      this.scheduleDeferredAuthReconnectCheck(),
    );
    this.socket.on("streamError", () =>
      this.scheduleDeferredAuthReconnectCheck(),
    );
    this.socket.on("messageStopped", () =>
      this.scheduleDeferredAuthReconnectCheck(),
    );
    // NOTE: NSAI-5117 — `streamFinish` is not always the last word. A turn that
    // produced an image is still counted as streaming by the mobile predicate
    // until the final `messageUpdated` carries the file, so the check above
    // runs while the turn is still active and re-arms nothing. Without this the
    // deferral would hold until some later turn ended, leaving the socket on
    // the handshake token the refresh was meant to replace.
    this.socket.on("messageUpdated", () =>
      this.scheduleDeferredAuthReconnectCheck(),
    );

    this.socket.on("connect_error", async (error) => {
      console.debug("WebSocket connection error:", error);
      if (!this.isAuthError(error)) return;

      // NOTE: NSAI-5910 — only the first refusal since the last good handshake
      // refreshes at once. A refusal that follows a refresh means a new token
      // did not help, so the next attempt waits (and, while the app is hidden,
      // waits for the user to come back). A send buffered meanwhile is failed
      // now rather than left waiting on a reconnect that may be minutes away.
      if (this.authRecovery.recordAuthRejection() === "now") {
        await this.handleAuthError();
        return;
      }

      console.debug(
        "WebSocket: handshake refused again — deferring the next auth retry",
      );
      this.notifyPendingMessagesFailed("auth-refresh-failed");
      this.pendingMessages = [];
    });

    this.socket.on("disconnect", (reason) => {
      console.debug("Disconnected from WebSocket server. Reason:", reason);
      // NOTE: NSAI-5909 — consumers mark in-flight streams as interrupted on
      // disconnect; frames received before the drop must land first or they
      // would "un-interrupt" the message afterwards.
      this.onFlushStreamBuffer?.();
      if (reason === "io server disconnect") {
        // NOTE: Server disconnected us, might be due to expired token
        this.updateAuthAndReconnect();
      }
    });

    this.socket.on("error", (error) => {
      console.debug("WebSocket error:", error);
    });

    // NOTE: NSAI-5042 — Nest WsExceptions from guarded events arrive here in
    // several shapes ({errorCode}, {message:{errorCode}}, or a string). A
    // guarded event rejected on a stale handshake token was previously logged
    // and dropped — the send died silently. React to the auth code with a
    // token refresh + reconnect; the fresh connect flushes pendingMessages.
    this.socket.on("exception", (data: unknown) => {
      const extractErrorCode = (payload: unknown): string | undefined => {
        let code: string | undefined;
        if (typeof payload === "object" && payload !== null) {
          const obj = payload as Record<string, unknown>;
          if (typeof obj.errorCode === "string") {
            code = obj.errorCode;
          } else if (typeof obj.message === "object" && obj.message !== null) {
            const nested = (obj.message as Record<string, unknown>).errorCode;
            code = typeof nested === "string" ? nested : undefined;
          }
        }
        return code;
      };

      if (extractErrorCode(data) === WS_AUTH_UNAUTHORIZED_ERROR_CODE) {
        const nowMs = Date.now();
        if (nowMs - this.lastAuthReconnectAtMs < AUTH_RECONNECT_THROTTLE_MS) {
          console.debug(
            "WebSocket: auth exception within throttle window — skipping reconnect",
          );
          return;
        }
        this.lastAuthReconnectAtMs = nowMs;
        console.debug(
          "WebSocket: guarded event rejected with stale auth — refreshing token",
        );
        void this.handleAuthError();
        return;
      }
      // NOTE: NSAI-5117 — the gateway rejected a guarded event because the
      // licence expired. Handled by the host, which knows where to send the
      // user; a client that supplies no handler just logs it.
      if (extractErrorCode(data) === LICENSE_EXPIRED_ERROR_CODE) {
        if (this.onLicenseExpired) {
          this.onLicenseExpired();
          return;
        }
      }
      console.debug("WebSocket exception:", data);
    });

    // NOTE: NSAI-4786 — consumers that subscribed before this socket existed (or
    // before a reconnect replaced the previous one) are wired up here.
    this.attachRegisteredListeners();
  }

  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  disconnect(): void {
    // NOTE: NSAI-5117 — run teardown hooks BEFORE the socket closes, so a
    // consumer can still emit (leaving its rooms). Client-initiated disconnects
    // only: a transport drop does not run them, because the rooms it dropped
    // are re-joined by the next `connect()`.
    for (const handler of this.beforeDisconnectHandlers) {
      handler();
    }

    this.pendingAuthReconnect = false;
    this.authRefreshGeneration += 1;
    this.isRefreshing = false;
    this.authRecovery.stop();

    if (this.socket) {
      console.debug("Disconnecting from WebSocket server...");
      // NOTE: NSAI-5117 — disconnect FIRST so consumers still hear the
      // `disconnect` event and can mark an in-flight reply as interrupted
      // rather than leaving it spinning forever. Stripping the handlers first
      // swallowed that event on every client-initiated reconnect (language or
      // project switch).
      this.socket.disconnect();
      // NOTE: NSAI-4786 — then strip every handler from the instance being
      // discarded so it cannot keep reacting to buffered frames on its way out.
      // Nothing can arrive between these two statements — they are synchronous
      // and adjacent. Consumer subscriptions stay in `eventListeners` and are
      // re-attached by the next `connect()`, which is what makes `reconnect()`
      // safe for callers that subscribe once per mount.
      this.socket.removeAllListeners();
      this.socket = null;
    }
  }

  /**
   * NOTE: NSAI-5117 — runs just before a client-initiated `disconnect()`, while
   * the socket is still usable. Web uses it to tear down its background chat
   * trackers, which the local class did inside `disconnect()` before the two
   * implementations were merged.
   */
  onBeforeDisconnect(handler: () => void): () => void {
    this.beforeDisconnectHandlers.add(handler);

    return () => {
      this.beforeDisconnectHandlers.delete(handler);
    };
  }

  onConnect(callback: () => void): () => void {
    return this.addEventListener({ event: "connect", listener: callback });
  }

  onDisconnect(
    callback: (reason: Socket.DisconnectReason) => void,
  ): () => void {
    return this.addEventListener({ event: "disconnect", listener: callback });
  }

  joinChat(chatId: string): void {
    if (!this.socket) {
      console.debug("Socket not connected when trying to join chat");
      throw new Error("Socket not connected");
    }
    // NOTE: Store current chat ID for reconnection
    this.currentChatId = chatId;
    console.debug("Joining chat:", chatId);
    this.socket.emit("joinChat", chatId);
  }

  leaveChat(chatId: string): void {
    if (!this.socket) {
      console.debug(
        "Socket not connected when trying to leave chat, skipping.",
      );
      return;
    }
    // NOTE: Clear current chat ID
    if (this.currentChatId === chatId) {
      this.currentChatId = null;
    }
    console.debug("Leaving chat:", chatId);
    this.socket.emit("leaveChat", chatId);
  }

  // NOTE: NSAI-3308 — returns the generated request id. The caller needs it to
  // recognize the persisted user message that this send produces, which is the
  // only signal that may clear a pinned skill selection; the Socket.IO
  // acknowledgement below proves the gateway received the frame and nothing more.
  sendMessage(payload: ISendMessagePayload): string {
    // NOTE: NSAI-5117 — may throw to abort the send (web's E2E failure hook).
    this.beforeSendMessage?.();

    // NOTE: If socket is null (never connected), throw an error
    if (!this.socket) {
      console.debug("Socket not connected when trying to send message");
      throw new Error("Socket not connected");
    }

    // NOTE: NSAI-5117 — remember the send made before the chat had a uuid, so a
    // consumer can render it optimistically once the chat is created.
    if (!payload.chatId) {
      this._lastNewChatMessage = payload;
    }

    // NOTE: Generate unique requestId for deduplication on server
    const requestId = this.generateRequestId();
    const payloadWithRequestId: IMessagePayload = {
      ...payload,
      requestId,
    };

    // NOTE: Check both connected flag and disconnected flag for more reliable state check
    if (!this.socket.connected || this.socket.disconnected) {
      console.debug(
        "WebSocket: Socket not connected, buffering message. Connected:",
        this.socket.connected,
        "Disconnected:",
        this.socket.disconnected,
      );
      this.pendingMessages.push({
        payload: payloadWithRequestId,
        timestamp: Date.now(),
        attempts: 0,
      });

      // NOTE: Try to reconnect with fresh token
      this.updateAuthAndReconnect();
      return requestId;
    }

    // NOTE: NSAI-5042 — a refresh elsewhere may have rotated the token while
    // this socket still holds the old handshake. Guarded events would be
    // rejected by WsJwtAuthGuard, so buffer the message and reconnect with
    // fresh auth first; the connect flush re-sends it.
    // NOTE: socket.io types `auth` as object-or-factory; narrowing the factory
    // arm away makes the property access legal without a type assertion.
    const socketAuthToken =
      typeof this.socket.auth === "function"
        ? undefined
        : this.socket.auth.token;
    if (socketAuthToken !== `Bearer ${this.storageService.getToken()}`) {
      console.debug(
        "WebSocket: handshake token is stale — reconnecting before send",
      );
      this.pendingMessages.push({
        payload: payloadWithRequestId,
        timestamp: Date.now(),
        attempts: 0,
      });
      this.updateAuthAndReconnect();
      // NOTE: NSAI-3308 — buffered, not dropped: the connect flush re-sends this
      // exact payload, so the caller still gets the request id it needs to
      // recognize the user message this send will produce.
      return requestId;
    }

    console.debug("Sending message:", payloadWithRequestId);

    // NOTE: Use emit with timeout and acknowledgement for delivery confirmation
    // If server doesn't acknowledge within 10 seconds, buffer for retry
    // NOTE: NSAI-5042 — large/multi-file payloads (base64 over mobile links)
    // can legitimately take far longer than 10s to be fully received and
    // acked; the flat timeout produced retransmissions of the whole payload
    // and could escalate to a false "message could not be sent". Mirrors the
    // web client's file-aware selection.
    const sendAckTimeoutMs =
      this.getAckTimeoutMsOverride?.() ??
      ((payloadWithRequestId.files?.length ?? 0) > 0
        ? MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITH_FILES
        : MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES);
    const emittedAtMs = Date.now();
    const settleInflightSend = this.trackInflightSend(
      payloadWithRequestId.chatId ?? null,
    );
    this.socket
      .timeout(sendAckTimeoutMs)
      .emit(
        "sendMessage",
        stripSelectionsForWire(payloadWithRequestId),
        (err: Error | null) => {
          settleInflightSend();
          if (err) {
            // NOTE: NSAI-5042 — the chat's sends were cancelled (Stop / stall
            // watchdog) while this emit awaited its ack: do not buffer, or the
            // cancelled message would resurrect on a later flush.
            if (
              this.wasSendCancelledSince({
                chatId: payloadWithRequestId.chatId ?? null,
                sinceMs: emittedAtMs,
              })
            ) {
              console.debug(
                "WebSocket: ack timeout for a cancelled send — dropping",
              );
              return;
            }
            console.debug(
              "WebSocket: Message acknowledgement timeout, buffering for retry:",
              err.message,
            );
            // NOTE: Buffer with same requestId - server will deduplicate if already processed
            this.pendingMessages.push({
              payload: payloadWithRequestId,
              timestamp: Date.now(),
              attempts: 1,
            });
            // NOTE: NSAI-5042 — socket.connected lies for up to ~145s on a
            // half-open link (pingInterval 25s + pingTimeout 120s). Probe
            // liveness instead of hard-recycling a healthy socket (which used
            // to drop in-flight streams and miss chatCreated events).
            if (!this.socket?.connected || this.socket?.disconnected) {
              this.updateAuthAndReconnect();
            } else {
              this.verifyConnectionAfterAckTimeout();
            }
          } else {
            console.debug("WebSocket: Message acknowledged by server");
            this.recordAckedSend(payloadWithRequestId.chatId ?? null);
          }
        },
      );

    return requestId;
  }

  // NOTE: NSAI-5659 — a generated HTML page threw while it rendered in a
  // preview. Fire-and-forget: the server decides whether to repair it and
  // answers later with `messageUpdated`. Returns whether the report reached a
  // connected socket; one sent while offline is dropped, nothing waits on it.
  reportHtmlPageRenderError(payload: IHtmlPageRenderErrorPayload): boolean {
    if (!this.socket?.connected) {
      return false;
    }
    this.socket.emit(HTML_PAGE_RENDER_ERROR_EVENT, payload);
    return true;
  }

  stopMessage(chatUuid: string): void {
    if (!this.socket) {
      console.debug("Socket not connected when trying to stop message");
      throw new Error("Socket not connected");
    }
    // NOTE: NSAI-5042 — the user cancelled this turn; a buffered send for the
    // chat must not be resurrected by a later reconnect flush, and an emit
    // still awaiting its ack must not re-buffer either (see the cancel marker).
    this.pruneCancelledSendMarkers();
    this.cancelledSendAtByChatId.set(chatUuid, Date.now());
    this.pendingMessages = this.pendingMessages.filter(
      (pending) => pending.payload.chatId !== chatUuid,
    );
    this.socket.emit("stopMessage", chatUuid);
  }

  // NOTE: NSAI-5117 — this and the two summary/prompt emits below keep the flat
  // no-files ack timeout rather than consulting `getAckTimeoutMsOverride`. That
  // mirrors the web class this replaced, where the override reached only
  // `sendMessage` and its retry flush: those are the emits the E2E timing
  // compression drives, and the rest carry no files, so the pair collapses to
  // one value anyway.
  sendToolApprovalResponse(payload: ISendToolApprovalParams): void {
    if (!this.socket) {
      console.debug("Socket not connected when trying to send tool approval");
      throw new Error("Socket not connected");
    }
    this.socket
      .timeout(MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES)
      .emit("toolApprovalResponse", payload, (err: Error | null) => {
        if (err) {
          console.debug(
            "WebSocket: Tool approval acknowledgement timeout",
            err,
          );
        }
      });
  }

  onNewMessage(callback: (messages: IMessage[]) => void): () => void {
    return this.addEventListener({
      event: "newMessages",
      listener: callback,
    });
  }

  onMessageUpdated(callback: (message: IMessage) => void): () => void {
    return this.addEventListener({
      event: "messageUpdated",
      listener: callback,
    });
  }

  onMessageDelta(
    callback: (payload: IMessageDeltaPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "messageDelta",
      listener: callback,
    });
  }

  onMessageContentUpdate(
    callback: (payload: IMessageContentUpdatePayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "messageContentUpdate",
      listener: callback,
    });
  }

  onChatCreated(callback: (chat: IChat) => void): () => void {
    return this.addEventListener({
      event: "chatCreated",
      listener: callback,
    });
  }

  onChatTitleGenerated(
    callback: (chat: IChatTitleGenerated) => void,
  ): () => void {
    return this.addEventListener({
      event: "chatTitleGenerated",
      listener: callback,
    });
  }

  onError(callback: (error: IWebSocketError) => void): () => void {
    return this.addEventListener({
      event: "error",
      listener: callback,
    });
  }

  // NOTE: NSAI-3308 — live activation records for one assistant message. The same
  // records are returned by the chat history read, so a client merges by
  // `skillUuid` instead of appending (see `mergeSkillActivations`).
  onSkillActivations(
    callback: (payload: TSkillActivationsEvent) => void,
  ): () => void {
    return this.addEventListener({
      event: SKILL_ACTIVATIONS_SOCKET_EVENT,
      listener: callback,
    });
  }

  // NOTE: NSAI-3304 — live sandboxed script-run progress for one assistant message.
  // The same records are returned by the chat history read, so a client merges by
  // run `uuid` instead of appending (see `mergeSkillScriptRuns`).
  onSkillScriptRun(
    callback: (payload: TSkillScriptRunEvent) => void,
  ): () => void {
    return this.addEventListener({
      event: SKILL_SCRIPT_RUN_SOCKET_EVENT,
      listener: callback,
    });
  }

  // NOTE: NSAI-6542 — answers an Auto confirmation card. The server takes the
  // first answer for the card pending in that chat; the waiting turn resumes
  // with the capability (accept) or without it (answer normally).
  // NOTE: Resolves with whether the server took the answer. It refuses a
  // stale card, a second answer and another member's answer, and no
  // resolution follows a refusal, so the caller has to re-enable the card.
  sendAutoConfirmationResponse(
    payload: ISendAutoConfirmationResponseParams,
  ): Promise<boolean> {
    const { socket } = this;
    if (!socket) {
      console.debug(
        "Socket not connected when trying to answer an Auto confirmation",
      );
      throw new Error("Socket not connected");
    }
    return new Promise((resolve) => {
      socket
        .timeout(MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES)
        .emit(
          AUTO_CONFIRMATION_RESPONSE_SOCKET_EVENT,
          payload,
          (err: Error | null, response?: { isAccepted?: boolean }) => {
            if (err) {
              console.debug(
                "WebSocket: Auto confirmation acknowledgement timeout",
                err,
              );
            }
            resolve(!err && response?.isAccepted === true);
          },
        );
    });
  }

  onAutoConfirmationRequest(
    callback: (payload: IAutoConfirmationRequestPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: AUTO_CONFIRMATION_REQUEST_SOCKET_EVENT,
      listener: callback,
    });
  }

  onAutoConfirmationResolved(
    callback: (payload: IAutoConfirmationResolvedPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: AUTO_CONFIRMATION_RESOLVED_SOCKET_EVENT,
      listener: callback,
    });
  }

  onToolCall(callback: (payload: IToolCallPayload) => void): () => void {
    return this.addEventListener({
      event: "toolCall",
      listener: callback,
    });
  }

  // NOTE: NSAI-5473 — one step of the transcript ended (completed, failed or
  // declined). `toolCall` only ever said a call started.
  onToolCallEnd(callback: (payload: IToolCallEndPayload) => void): () => void {
    return this.addEventListener({
      event: TOOL_CALL_END_SOCKET_EVENT,
      listener: callback,
    });
  }

  onToolApprovalRequest(
    callback: (payload: IToolApprovalRequestPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "toolApprovalRequest",
      listener: callback,
    });
  }

  onToolApprovalResponded(
    callback: (payload: IToolApprovalRespondedPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "toolApprovalResponded",
      listener: callback,
    });
  }

  onToolFile(callback: (payload: IToolFilePayload) => void): () => void {
    return this.addEventListener({
      event: "toolFile",
      listener: callback,
    });
  }

  onToolFileError(
    callback: (payload: IToolFileErrorPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "toolFileError",
      listener: callback,
    });
  }

  onReasoningStep(
    callback: (payload: IReasoningStepPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "reasoningStep",
      listener: callback,
    });
  }

  onStreamError(callback: (payload: IStreamErrorPayload) => void): () => void {
    return this.addEventListener({
      event: "streamError",
      listener: callback,
    });
  }

  onStreamFinish(
    callback: (payload: IStreamFinishPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "streamFinish",
      listener: callback,
    });
  }

  onDeepSearchProgress(
    callback: (payload: IDeepSearchProgressPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "deepSearchProgress",
      listener: callback,
    });
  }

  onWebSearchProgress(
    callback: (payload: IWebSearchProgressPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "webSearchProgress",
      listener: callback,
    });
  }

  onMessagePreparationProgress(
    callback: (payload: IMessagePreparationProgressPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "messagePreparationProgress",
      listener: callback,
    });
  }

  requestMultiModelSummary(payload: IRequestMultiModelSummaryPayload): void {
    if (!this.socket) {
      console.debug(
        "Socket not connected when trying to request multi-model summary",
      );
      throw new Error("Socket not connected");
    }
    this.socket
      .timeout(MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES)
      .emit("requestMultiModelSummary", payload, (err: Error | null) => {
        if (err) {
          console.debug(
            "WebSocket: Summary request acknowledgement timeout",
            err,
          );
        }
      });
  }

  improvePrompt(
    payload: IImprovePromptPayload,
    onAckError?: (err: Error) => void,
  ): void {
    if (!this.socket) {
      console.debug("Socket not connected when trying to improve prompt");
      throw new Error("Socket not connected");
    }
    this.socket
      .timeout(MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES)
      .emit("improvePrompt", payload, (err: Error | null) => {
        if (err) {
          console.debug(
            "WebSocket: Improve prompt acknowledgement timeout",
            err,
          );
          // NOTE: Surface ack failures so the consumer can unwind the
          // streaming UI — without this the hook stays stuck in `isImproving`
          // forever when the server handler is unreachable or silently broken.
          onAckError?.(err);
        }
      });
  }

  cancelImprovePrompt(requestId: string): void {
    if (!this.socket) {
      console.debug(
        "Socket not connected when trying to cancel improve prompt",
      );
      return;
    }
    // NOTE: Fire-and-forget — no ack callback. The server is responsible for
    // aborting its AbortController; the client UI has already been reset.
    this.socket.emit("cancelImprovePrompt", { requestId });
  }

  onPromptImproveDelta(
    callback: (payload: IPromptImproveDeltaPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "promptImproveDelta",
      listener: callback,
    });
  }

  onPromptImproveFinish(
    callback: (payload: IPromptImproveFinishPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "promptImproveFinish",
      listener: callback,
    });
  }

  onPromptImproveError(
    callback: (payload: IPromptImproveErrorPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "promptImproveError",
      listener: callback,
    });
  }

  // NOTE: NSAI-5820 — how full the model's context window is for this chat,
  // emitted by the server's fit-guard as it assembles a turn. Registered
  // through addEventListener rather than straight onto the socket so the
  // subscription survives a reconnect: a banner that goes deaf after a dropped
  // connection would stop warning exactly when a long chat needs it.
  onContextPressure(
    callback: (payload: IContextPressurePayload) => void,
  ): () => void {
    return this.addEventListener({
      event: CONTEXT_PRESSURE_SOCKET_EVENT,
      listener: callback,
    });
  }

  // NOTE: NSAI-6974 — the images of a user message whose description had
  // personal data masked. Through addEventListener so it survives a reconnect.
  onPiiImageMasked(
    callback: (payload: IPiiImageMaskedPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: PII_IMAGE_MASKED_SOCKET_EVENT,
      listener: callback,
    });
  }

  // NOTE: NSAI-7345 — the attachments of a user message whose PDF yielded too
  // little text for its page count. Through addEventListener so it survives a
  // reconnect.
  onAttachmentTextExtractionWarning(
    callback: (payload: IAttachmentTextExtractionWarningPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: ATTACHMENT_TEXT_EXTRACTION_WARNING_SOCKET_EVENT,
      listener: callback,
    });
  }

  // NOTE: NSAI-6883 — the attachments this turn answered without, or with only
  // the beginning of, because together they did not fit. Registered through
  // addEventListener for the same reconnect reason as onContextPressure.
  onAttachmentsOmitted(
    callback: (payload: IAttachmentsOmittedPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: ATTACHMENTS_OMITTED_SOCKET_EVENT,
      listener: callback,
    });
  }

  onKnowledgeSources(
    callback: (payload: IKnowledgeSourcesPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: "knowledgeSources",
      listener: callback,
    });
  }

  // NOTE: NSAI-5681 — one "connect this app" prompt for an assistant message.
  // Merged by provider on the client, never appended: the
  // history read returns the same records for the same message.
  onIntegrationConnectPrompt(
    callback: (payload: IIntegrationConnectPromptPayload) => void,
  ): () => void {
    return this.addEventListener({
      event: INTEGRATION_CONNECT_PROMPT_SOCKET_EVENT,
      listener: callback,
    });
  }

  onMessagesDeleted(
    callback: (payload: { chatUuid: string; count: number }) => void,
  ): () => void {
    return this.addEventListener({
      event: "messagesDeleted",
      listener: callback,
    });
  }

  onMessageStopped(
    callback: (payload: { chatUuid: string }) => void,
  ): () => void {
    return this.addEventListener({
      event: "messageStopped",
      listener: callback,
    });
  }
}
